import { computeFees, MTK_DECIMALS } from '@meteraprotocol/core';
import { BigRational } from 'big-rational-ts';
import BigNumber from 'bignumber.js';
import { useMemo } from 'react';
import { getMTKPrice } from '../blockchain/client/index.js';
import {
  computeAmountPerAssetForInteraction,
  formatState,
} from '../blockchain/client/interact.js';
import { IPortfolioState } from '../types.js';
import { bigNumberToBigRational, bigRationalToBigNumber } from '../utils.js';

type useBuySellFeesProps = {
  inputValue: BigNumber;
  portfolio: IPortfolioState;
  type: 'mint' | 'burn';
};

type useBuySellFeesReturn = {
  amountsPerAsset: {
    assets: Record<string, bigint>;
    mtkSupply: bigint;
  };
  fees: {
    batcherFee: BigNumber;
    platformFee: BigNumber;
    userFee: BigNumber;
  };
  formattedState: ReturnType<typeof formatState>;
  mintedMicroMTK: BigNumber;
  userReceiveMTK: BigNumber;
  withdrawLovelace: BigNumber;
  withdrawUserLvc: BigNumber;
};

function useBuySellFees({
  inputValue,
  portfolio,
  type,
}: useBuySellFeesProps): useBuySellFeesReturn {
  const formattedState = useMemo(() => formatState(portfolio), [portfolio]);

  const {
    amountsPerAsset,
    mintedMicroMTK,
    userReceiveMTK,
    fees,
    withdrawLovelace,
    withdrawUserLvc,
  } = useMemo(() => {
    const rationalInput = bigNumberToBigRational(inputValue);
    const adaMicroMTKPrice = getMTKPrice(portfolio);

    // ada worth of the interaction
    const adaAmount =
      type === 'mint'
        ? rationalInput
        : rationalInput
            .negate()
            .mul(new BigRational(BigInt(10 ** MTK_DECIMALS), 1n))
            .mul(adaMicroMTKPrice)
            .reduce();

    // how much of each asset this interaction involves
    const amountsPerAsset = computeAmountPerAssetForInteraction(
      adaAmount,
      formattedState,
    );

    const fees = computeFees({
      portfolioState: {
        batcherFee: BigNumber(portfolio.batcherFee.toString()),
        entryFee: BigNumber(portfolio.entryFee.toString()),
        exitFee: BigNumber(portfolio.exitFee.toString()),
        platformFee: BigNumber(portfolio.platformFee.toString()),
        // microMTKPrice is expected in lovelace
        microMtkPrice: bigRationalToBigNumber(adaMicroMTKPrice).multipliedBy(
          10 ** 6,
        ),
      },
      amount: bigRationalToBigNumber(
        adaAmount.mul(new BigRational(BigInt(10 ** 6), 1n)).reduce(),
      ),
      type,
    });

    const withdrawLovelace = bigRationalToBigNumber(adaAmount)
      .multipliedBy(10 ** 6)
      .negated();

    const depositMicroMTK = inputValue.div(
      bigRationalToBigNumber(adaMicroMTKPrice),
    );

    return {
      amountsPerAsset,
      mintedMicroMTK: depositMicroMTK,
      // how much the user gets in micro MTK for a deposit
      userReceiveMTK: depositMicroMTK.minus(fees.userFee),
      withdrawLovelace,
      // how much the user gets in lovelace for a withdraw
      withdrawUserLvc: withdrawLovelace.minus(
        fees.userFee
          .multipliedBy(bigRationalToBigNumber(adaMicroMTKPrice))
          .multipliedBy(10 ** 6),
      ),
      fees,
    };
  }, [inputValue, portfolio, type]);

  return {
    amountsPerAsset,
    fees,
    formattedState,
    /**
     * How many MTKs are involved in the order (if it's a deposit)
     * What the user gets + entry fee
     */
    mintedMicroMTK,
    /**
     * How many MTKs the user will receive (if it's a deposit)
     */
    userReceiveMTK,
    /**
     * How much worth (in lovelace) is being extracted from the portfolio in the order (if it's a withdraw)
     */
    withdrawLovelace,
    /**
     * How much worth (in lovelace) the user will receive (if it's a withdraw)
     */
    withdrawUserLvc,
  };
}

export default useBuySellFees;
